import React from 'react';
import { css } from '@emotion/react';
import { TextField } from '@mui/material';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';
import { Dayjs } from 'dayjs';
import { color } from '../../../../styles';
import { IGetResidentRes } from '../../../../types/api/resident/getResident';
import { IGetNursingHomeRes } from '../../../../types/api/nursingHome/getNursingHome';
import { UrineGraph } from './urineGraph';
import { DatetimeActivityStatusGraph } from './datetimeActivityStatusGraph';
import { UrineTrendHeatmapGraph } from './urineTrendHeatmap';
import { OverThresholdHeatmapGraph } from './overThresholdHeatmap';
import { DetectUrinationHeatmapGraph } from './detectUrinationHeatmap';
import { AverageHeatmapGraph } from './averageHeatmap';
import { CareRecordStackbarGraph } from './careRecordStackbarGraph';
import { CareRecordPieGraph } from './careRecordPieGraph';
import {
  ActivityStatusGraphCaption,
  NotificationCaption,
  PlaceCaption,
  UrineTrendGraphCaption,
} from './caption';
import TimeRangeDisplays from './timeRangeDisplay';
import { ActivityStatusHeatmapGraph } from './activityStatusHeatmap';
import { UrineReportData } from './createUrineReport';
import { getPrintPx, PRINT_CONTENT_SCALE } from './printChartOptions';

const FlexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormContent = css`
  width: 75%;
  flex: 1 1 75%;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-left: 24px;
  margin-bottom: 8px;
  overflow-wrap: break-word;
`;

const MemoText = (isPrinting: boolean) => css`
  width: 75%;
  margin-left: ${getPrintPx(24, isPrinting)}px;
  margin-bottom: ${getPrintPx(16, isPrinting)}px;
  padding: ${getPrintPx(8, isPrinting)}px ${getPrintPx(12, isPrinting)}px;
  border: 1px solid ${color.border_gray};
  border-radius: 4px;
  white-space: pre-wrap;
  font-size: ${getPrintPx(14, isPrinting)}px;
`;

const LowerContainer = (isPrinting: boolean) => css`
  display: flex;
  flex-direction: column;
  margin: ${getPrintPx(16, isPrinting)}px;
  gap: ${getPrintPx(12, isPrinting)}px;
`;

const GraphCard = (isPrinting: boolean) => css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${getPrintPx(12, isPrinting)}px ${getPrintPx(8, isPrinting)}px;
  background-color: ${color.light_gray};
  border: 1px solid ${color.border_gray};
  border-radius: 8px;
`;

const GraphTitle = (isPrinting: boolean) => css`
  color: ${color.dark_gray};
  text-align: center;
  font-weight: bold;
  font-size: ${getPrintPx(20, isPrinting)}px;
`;

const CaptionContainer = (isPrinting: boolean) => css`
  margin: ${getPrintPx(16, isPrinting)}px ${getPrintPx(16, isPrinting)}px ${getPrintPx(24, isPrinting)}px;
  font-size: ${getPrintPx(12, isPrinting)}px;
`;

const StatisticsTitle = (isPrinting: boolean) => css`
  display: flex;
  justify-content: center;
  font-size: ${getPrintPx(14, isPrinting)}px;
  font-weight: bold;
`;

const StatisticsNumber = (isPrinting: boolean) => css`
  display: flex;
  align-items: baseline;
  justify-content: center;
  font-size: ${getPrintPx(24, isPrinting)}px;
  font-weight: bold;
`;

const ReportHeaderWrapperStyle = (isPrinting: boolean) => css`
  margin-bottom: ${getPrintPx(16, isPrinting)}px;
  padding: ${getPrintPx(8, isPrinting)}px;
  background-color: ${color.green};
  color: ${color.white};

  h1 {
    margin: 0;
    font-size: ${getPrintPx(20, isPrinting)}px;
    font-weight: bold;
  }
`;

const ReportInfoContainerStyle = (isPrinting: boolean) => css`
  flex: 1;
  text-align: left;

  & p {
    margin: ${getPrintPx(4, isPrinting)}px 0;
    font-size: ${getPrintPx(14, isPrinting)}px;
    font-weight: bold;
  }
`;

const ReportIconStyle = (isPrinting: boolean) => css`
  width: ${getPrintPx(42, isPrinting)}px;
  height: auto;
  object-fit: contain;
`;

const TitleWithLinesStyle = (isPrinting: boolean) => css`
  display: flex;
  align-items: center;
  gap: ${getPrintPx(16, isPrinting)}px;
  width: 100%;
  padding: 0 ${getPrintPx(16, isPrinting)}px;
`;

const TitleTextStyle = (isPrinting: boolean) => css`
  white-space: nowrap;
  font-size: ${getPrintPx(20, isPrinting)}px;
  font-weight: bold;
  color: #444;
`;

const CareRecordCaptionContainer = (isPrinting: boolean) => css`
  display: flex;
  flex-direction: column;
  margin: ${getPrintPx(8, isPrinting)}px ${getPrintPx(16, isPrinting)}px ${getPrintPx(24, isPrinting)}px;
  gap: ${getPrintPx(8, isPrinting)}px 0;
  font-size: ${getPrintPx(12, isPrinting)}px;
`;

const Caution = css`
  ${FlexCenter};
  width: 100%;
  height: 120px;
`;

const Warning = (isPrinting: boolean) => css`
  ${FlexCenter};
  color: ${color.red};
  width: 100%;
  height: ${getPrintPx(64, isPrinting)}px;
  font-size: ${getPrintPx(14, isPrinting)}px;
`;

const HeatmapContainer = css``;

const CareRecordGraphContainer = css`
  display: flex;
  gap: 12px;
  width: 100%;

  @media print {
    break-inside: auto;
    page-break-inside: auto;
  }
`;

const ContentContainer = css`
  break-inside: avoid;
`;

const PrintSectionHeatmap = css`
  break-inside: avoid;

  @media print {
    page-break-after: always;
  }
`;

const PrintSectionUrineGraph = css`
  @media print {
    page-break-before: always;
  }
`;

const PrintSectionSupport = css`
  @media print {
    page-break-before: always;
    break-inside: auto;
    page-break-inside: auto;
  }
`;

const CaptionTop = css`
  display: flex;
  flex-wrap: wrap;
`;

const CaptionTopLeft = (isPrinting: boolean) => css`
  display: flex;
  flex-direction: column;
  width: 60%;
  gap: ${getPrintPx(8, isPrinting)}px 0;
  margin-bottom: ${getPrintPx(8, isPrinting)}px;

  @media print {
    width: 100%;
  }
`;

const PrintCaptionScale = (isPrinting: boolean) =>
  isPrinting &&
  css`
    width: ${100 / PRINT_CONTENT_SCALE}%;
    box-sizing: border-box;
    zoom: ${PRINT_CONTENT_SCALE};
  `;

const SupportGraphTitle = css`
  display: flex;
  flex-direction: column;
`;

const GraphSubtitleStyle = (isPrinting: boolean) => css`
  font-size: ${getPrintPx(14, isPrinting)}px;
  color: ${color.dark_gray};
  text-align: center;
`;

const StatisticsContainer = css`
  display: flex;
  width: 100%;
  justify-content: space-around;
  flex-wrap: wrap;
`;

const ReportHeaderContainerStyle = css`
  display: flex;
  width: 100%;
  align-items: center;
`;

const ReportTitleContainerStyle = (isCompact: boolean) => css`
  flex: ${isCompact ? 1 : 1.4};
`;

const ReportTitleInnerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-right: 12px;
`;

const PrintHeaderStyle = (isPrinting: boolean) => css`
  gap: ${getPrintPx(8, isPrinting)}px;
`;

const LineStyle = css`
  width: 100%;
  border-top: 1px solid #ccc;
`;

const PrintLeftLineStyle = css`
  flex: 1;
`;

const PrintRightLineStyle = css`
  flex: 1;
`;

export interface UrineTrendReportContentRefs {
  heatmapRef?: React.RefObject<HTMLDivElement>;
  urineGraphRef?: React.RefObject<HTMLDivElement>;
  lowerContainerRef?: React.RefObject<HTMLDivElement>;
  reportHeaderRef?: React.RefObject<HTMLDivElement>;
}

export interface UrineTrendReportContentProps {
  isPrinting: boolean;
  useStaticMemo: boolean;
  urineReportData?: UrineReportData;
  resident?: IGetResidentRes;
  currentNursingHome: IGetNursingHomeRes;
  startDate: Dayjs;
  endDate: Dayjs;
  memoText: string;
  showMemoBox: boolean;
  showThresholdMap: boolean;
  showAverageMap: boolean;
  showCareRecordPlot: boolean;
  showNotificationPlot: boolean;
  showSensorResample: boolean;
  showActivityStatus: boolean;
  graphSubtitle: React.ReactNode;
  latestSensorReactionEntries: [string, UrineReportData['sensorReactionArray'][string]][];
  sensorReactionEntryCount: number;
  onMemoChange?: (value: string) => void;
  renderBeforeUrineGraphs?: React.ReactNode;
  refs?: UrineTrendReportContentRefs;
}

export const UrineTrendReportContent: React.FC<UrineTrendReportContentProps> = ({
  isPrinting,
  useStaticMemo,
  urineReportData,
  resident,
  currentNursingHome,
  startDate,
  endDate,
  memoText,
  showMemoBox,
  showThresholdMap,
  showAverageMap,
  showCareRecordPlot,
  showNotificationPlot,
  showSensorResample,
  showActivityStatus,
  graphSubtitle,
  latestSensorReactionEntries,
  sensorReactionEntryCount,
  onMemoChange,
  renderBeforeUrineGraphs,
  refs,
}) => {
  const { t, i18n, serviceT } = useServiceTranslation();

  const ReportHeader = () => (
    <div css={ReportHeaderWrapperStyle(isPrinting)} ref={refs?.reportHeaderRef}>
      <div css={ReportHeaderContainerStyle}>
        <div css={ReportTitleContainerStyle(isPrinting)}>
          <div css={ReportTitleInnerStyle}>
            <img src='/images/residents/U2P筐体_LED付1.png' alt='Report Icon' css={ReportIconStyle(isPrinting)} />
            <h1>{serviceT('urination&ActivityTrendAnalysisReport')}</h1>
          </div>
        </div>
        <div css={ReportInfoContainerStyle(isPrinting)}>
          <p>
            {t('facility/Unit', '施設/ユニット')}: {currentNursingHome.name}/{resident?.nursingHomeUnitName}
          </p>
          <p>
            {t('resident', '入居者')}: {resident?.lastName} {resident?.name}
            {i18n.language.startsWith('ja')
              ? `${resident?.name && !resident.name.match(/(さま|様)$/) ? 'さま' : ''}`
              : ''}
          </p>
          <p>
            {t('period', '期間')}: {startDate.tz('Asia/Tokyo').format('YYYY/MM/DD')} -{' '}
            {endDate.tz('Asia/Tokyo').format('YYYY/MM/DD')}（{urineReportData?.heatmapData.yCategories.length}
            {t('forDays', '日分')}）
          </p>
        </div>
      </div>
    </div>
  );

  const ContentHeader = ({ title }: { title: string }) => (
    <div css={[TitleWithLinesStyle(isPrinting), isPrinting && PrintHeaderStyle(isPrinting)]}>
      <div css={[LineStyle, isPrinting && PrintLeftLineStyle]} />
      <h2 css={TitleTextStyle(isPrinting)}>{title}</h2>
      <div css={[LineStyle, isPrinting && PrintRightLineStyle]} />
    </div>
  );

  return (
    <>
      {urineReportData?.hasGraphData.heatmap ? (
        <div css={PrintSectionHeatmap} ref={refs?.heatmapRef}>
          <ReportHeader />
          {showMemoBox &&
            (useStaticMemo ? (
              memoText ? (
                <div css={MemoText(isPrinting)}>{memoText}</div>
              ) : null
            ) : (
              <div css={FormContent}>
                <TextField
                  sx={{ width: '100%' }}
                  label={t('memo', 'メモ')}
                  multiline={true}
                  value={memoText}
                  onChange={(e) => onMemoChange?.(e.target.value)}
                />
              </div>
            ))}
          <ContentHeader title={serviceT('urinationTrendAnalysis')} />
          <TimeRangeDisplays detectUrinationHeatmap={urineReportData.detectUrinationFlags} isPrinting={isPrinting} />
          <div css={ContentContainer}>
            <div css={HeatmapContainer}>
              <DetectUrinationHeatmapGraph
                detectUrinationHeatmap={urineReportData.detectUrinationFlags}
                isPrinting={isPrinting}
              />
              {showThresholdMap && (
                <OverThresholdHeatmapGraph
                  overThresholdHeatmap={urineReportData.overThresholdHeatmap}
                  isPrinting={isPrinting}
                />
              )}
              {showAverageMap && (
                <AverageHeatmapGraph averageHeatmap={urineReportData.averageHeatmap} isPrinting={isPrinting} />
              )}
              <UrineTrendHeatmapGraph
                heatmapData={urineReportData.heatmapData}
                careRecordPlot={urineReportData.careRecordPlotPoints}
                notificationPlot={urineReportData.notificationPlotPoints}
                showCareRecordPlot={showCareRecordPlot}
                showNotificationPlot={showNotificationPlot}
                isPrinting={isPrinting}
              />
            </div>
          </div>
          <div css={[CaptionContainer(false), PrintCaptionScale(isPrinting)]}>
            <div css={CaptionTop}>
              <div css={CaptionTopLeft(false)}>
                <NotificationCaption />
                <PlaceCaption />
              </div>
              <UrineTrendGraphCaption />
            </div>
          </div>
          {showActivityStatus && (
            <div css={ContentContainer}>
              <ContentHeader title={serviceT('activityAnalysis')} />
              <ActivityStatusHeatmapGraph heatmapData={urineReportData.activityHeatmap} isPrinting={isPrinting} />
              <div css={[CareRecordCaptionContainer(false), PrintCaptionScale(isPrinting)]}>
                <ActivityStatusGraphCaption />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div css={Caution}>{t('noData', 'データがありません')}</div>
      )}

      {urineReportData?.hasGraphData.urineGraph ? (
        <div css={PrintSectionUrineGraph}>
          <ContentHeader title={t('urineAccumulationGraph', '尿のたまり具合グラフ')} />
          {renderBeforeUrineGraphs}
          <div ref={refs?.urineGraphRef}>
            {latestSensorReactionEntries.map(([date, entries]) => (
              <div key={date} css={ContentContainer}>
                <UrineGraph
                  start={entries.start}
                  end={entries.end}
                  title={date.split('/').slice(1).join('/')}
                  sensorData={entries.sensorData}
                  resampleData={entries.resampleData}
                  notificationData={urineReportData.notificationScatter}
                  careRecordData={urineReportData.careRecordScatter}
                  residualUrineData={urineReportData.residualUrineScatter}
                  bladderThreshold={resident?.bladderThresholdPercentage}
                  showResampleData={showSensorResample}
                  showNotificationData={showNotificationPlot}
                  showCareRecordData={showCareRecordPlot}
                  isPrinting={isPrinting}
                />
                {showActivityStatus && (
                  <DatetimeActivityStatusGraph
                    start={entries.start}
                    end={entries.end}
                    title={date.split('/').slice(1).join('/')}
                    activityStatusData={entries.postureData}
                    isPrinting={isPrinting}
                  />
                )}
              </div>
            ))}
            {sensorReactionEntryCount > 60 && (
              <div css={Warning(isPrinting)}>{t('displayingTheLast60Graphs', '直近60件のグラフを表示しています')}</div>
            )}
          </div>
        </div>
      ) : (
        <div css={Caution}>{t('noData', 'データがありません')}</div>
      )}

      {urineReportData?.hasGraphData.supportGraph ? (
        <div css={[LowerContainer(isPrinting), PrintSectionSupport]} ref={refs?.lowerContainerRef}>
          <div css={CareRecordGraphContainer}>
            <div css={[FlexCenter, GraphCard(isPrinting), { width: '60%' }]}>
              <div css={SupportGraphTitle}>
                <span css={GraphTitle(isPrinting)}>{t('FrequencyOfCareProvided', 'ケア介入回数')}</span>
                <div css={GraphSubtitleStyle(isPrinting)}>{graphSubtitle}</div>
              </div>
              <CareRecordStackbarGraph
                graphData={urineReportData.careRecordGraphData.stackbarData}
                xCategories={urineReportData.careRecordGraphData.xCategories}
                isPrinting={isPrinting}
              />
              <div css={StatisticsContainer}>
                {urineReportData.careRecordGraphData.careCountData.map((v, i) => (
                  <div key={i}>
                    <div css={StatisticsTitle(isPrinting)}>{v.name ?? ''}</div>
                    <div css={StatisticsNumber(isPrinting)}>{v.data ?? ''}</div>
                  </div>
                ))}
              </div>
            </div>
            <div css={[FlexCenter, GraphCard(isPrinting), { width: '39%' }]}>
              <div css={SupportGraphTitle}>
                <span css={GraphTitle(isPrinting)}>{t('UrinationStatus', '排尿状況')}</span>
                <div css={GraphSubtitleStyle(isPrinting)}>{graphSubtitle}</div>
              </div>
              <CareRecordPieGraph
                graphData={urineReportData.careRecordGraphData.pieData}
                xCategories={urineReportData.careRecordGraphData.xCategories}
                isPrinting={isPrinting}
              />
              <div css={StatisticsContainer}>
                {urineReportData.careRecordGraphData.statisticsData.map((v, i) => (
                  <div key={i}>
                    <div css={StatisticsTitle(isPrinting)}>{v.name ?? ''}</div>
                    <div css={StatisticsNumber(isPrinting)}>{typeof v.data === 'number' ? `${v.data}%` : (v.data ?? '')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div css={Caution}>{t('noData', 'データがありません')}</div>
      )}
    </>
  );
};
